import { useEffect, useMemo, useState } from 'react';
import { FinanceiroService } from './Service';
import { formatMoneyBRL, toDateInputValue } from './utils';
import { FinanceWarning, MissingCostWarning, PendingPostingsWarning } from './FinanceWarning';

const service = new FinanceiroService();

export default function Dre() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dre, setDre] = useState<any>(null);

    const defaultTo = useMemo(() => new Date(), []);
    const defaultFrom = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    }, []);

    const [from, setFrom] = useState<string>(toDateInputValue(defaultFrom));
    const [to, setTo] = useState<string>(toDateInputValue(defaultTo));

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await service.dre({ from, to });
            setDre(res?.data || null);
        } catch (e: any) {
            setError(String(e?.response?.data?.message || 'Erro ao carregar DRE'));
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totals = dre?.totals || {};
    const appointmentsWithoutRate = Number(dre?.warnings?.appointmentsWithoutCommissionRate || 0);
    const revenueDifference = Number(dre?.warnings?.serviceRevenueDifference || 0);

    return (
        <div className="flex flex-col gap-4">
            <div className="card bg-base-100 shadow">
                <div className="card-body py-3">
                    <div className="flex flex-col gap-3 md:flex-row md:items-end">
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">De</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="label py-1">
                                <span className="label-text">Até</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full md:w-44"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                            />
                        </div>
                        <button className="btn btn-sm btn-primary md:ml-auto" onClick={load}>
                            Atualizar
                        </button>
                    </div>
                    {error && (
                        <div className="alert alert-error mt-3">
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            </div>

            {!loading && <MissingCostWarning missingCost={dre?.warnings?.missingCost} />}
            {!loading && <PendingPostingsWarning count={dre?.warnings?.pendingPostings} />}
            {!loading && revenueDifference !== 0 && (
                <FinanceWarning>
                    A receita de serviços está {formatMoneyBRL(Math.abs(revenueDifference))} {revenueDifference > 0 ? 'acima' : 'abaixo'} do
                    faturamento dos dashboards neste período. Para acertar, use Reprocessar lançamentos em Configurações.
                </FinanceWarning>
            )}
            {!loading && appointmentsWithoutRate > 0 && (
                <FinanceWarning>
                    {appointmentsWithoutRate === 1 ? '1 atendimento concluído' : `${appointmentsWithoutRate} atendimentos concluídos`} sem
                    comissão configurada para o profissional na data do atendimento. Eles não entram no total de comissões.
                </FinanceWarning>
            )}

            <div className="card bg-base-100 shadow">
                <div className="card-body">
                    {loading ? (
                        <div>Carregando...</div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <div className="flex justify-between">
                                <div className="opacity-70">Receita (serviços)</div>
                                <div className="font-medium">{formatMoneyBRL(totals.revenueServices || 0)}</div>
                            </div>
                            <div className="flex justify-between">
                                <div className="opacity-70">Receita (produtos)</div>
                                <div className="font-medium">{formatMoneyBRL(totals.revenueProducts || 0)}</div>
                            </div>
                            <div className="flex justify-between">
                                <div className="opacity-70">Receita (assinaturas)</div>
                                <div className="font-medium">{formatMoneyBRL(totals.revenueSubscriptions || 0)}</div>
                            </div>
                            <div className="divider my-1" />
                            <div className="flex justify-between">
                                <div className="opacity-70">Receita total</div>
                                <div className="font-semibold">{formatMoneyBRL(totals.revenueTotal || 0)}</div>
                            </div>
                            <div className="flex justify-between">
                                <div className="opacity-70">Custos (insumos)</div>
                                <div className="font-medium">{formatMoneyBRL(totals.cogsConsumables || 0)}</div>
                            </div>
                            <div className="flex justify-between">
                                <div className="opacity-70">Custos (revenda)</div>
                                <div className="font-medium">{formatMoneyBRL(totals.cogsResale || 0)}</div>
                            </div>
                            <div className="flex justify-between">
                                <div className="opacity-70">Custos totais</div>
                                <div className="font-semibold">{formatMoneyBRL(totals.cogsTotal || 0)}</div>
                            </div>
                            <div className="divider my-1" />
                            <div className="flex justify-between">
                                <div className="opacity-70">Lucro bruto estimado</div>
                                <div className="font-semibold">{formatMoneyBRL(totals.grossProfitEstimated || 0)}</div>
                            </div>
                            <div className="flex justify-between">
                                <div className="opacity-70">Comissões</div>
                                <div className="font-medium">{formatMoneyBRL(totals.commissionsTotal || 0)}</div>
                            </div>
                            <div className="flex justify-between">
                                <div className="opacity-70">Despesas</div>
                                <div className="font-medium">{formatMoneyBRL(totals.expensesTotal || 0)}</div>
                            </div>
                            <div className="divider my-1" />
                            <div className="flex justify-between">
                                <div className="opacity-70">Resultado operacional estimado</div>
                                <div className="font-semibold">{formatMoneyBRL(totals.operatingResultEstimated || 0)}</div>
                            </div>
                            <div className="text-xs opacity-70">
                                Compras de estoque no período: {formatMoneyBRL(totals.inventoryPurchasesTotal || 0)}. Não são somadas às
                                despesas, porque o custo entra quando o item é usado ou vendido.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

